import {Url, Controller} from '../controller';
import {Filter} from '../filter';
import {NullableRangeParameter} from '../parameter';
import {clamp} from '../helpers';

// A guess at the maximum word length (doesn't have to be exact but is basis for
// calculations).
const MAX_WORD_LENGTH = 20;

// A guess at the maximum number of words.
const MAX_NUM_WORDS = 10;

// A guess at the maximum char length of a domain.
const MAX_NUM_CHARS = 50;

export class CombinedWordLengthFilter extends Filter {
  $_params = [
    new NullableRangeParameter('Minimum character count', false, 5, 1, MAX_NUM_CHARS),
    new NullableRangeParameter('Maximum character count', false, 12, 1, MAX_NUM_CHARS),
    new NullableRangeParameter('Desired character count', false, 7, 1, MAX_NUM_CHARS),
  ];

  static $_title = 'Number of total characters';
  static $_description = 'Rank by domain length in characters';

  constructor() {
    super();
  }

  $_score(url: Url) {
    const minLength = this.$_params[0].$_value;
    const maxLength = this.$_params[1].$_value;
    const desiredLength = this.$_params[2].$_value;

    let totalScore = 0;
    let maxScore = 0;

    let totalLength = 0;
    url.iterate(this, word => {
      totalLength += word.label.length;
    });

    // Calculate score functions.
    if (minLength != null) {
      // Handle minimum length.
      if (totalLength < minLength) {
        // Negatively penalize when total length is under minimum.
        totalScore += clamp((totalLength - minLength) / MAX_NUM_CHARS, -1, 1);
      } else {
        totalScore++;
      }
      maxScore++;
    }

    if (maxLength != null) {
      // Handle maximum length.
      if (totalLength > maxLength) {
        // Negatively penalize when total length is above maximum.
        totalScore += clamp((maxLength - totalLength) / MAX_NUM_CHARS, -1, 1);
      } else {
        totalScore++;
      }
      maxScore++;
    }

    if (desiredLength != null) {
      // Handle desired length.
      if (totalLength != desiredLength) {
        // Negatively penalize when total length strays from desired length.
        totalScore += clamp(
          -Math.abs(totalLength - desiredLength) / MAX_NUM_CHARS,
          -1,
          1
        );
      } else {
        totalScore++;
      }
      maxScore++;
    }

    return totalScore / maxScore;
  }
}

export class NumWordsFilter extends Filter {
  $_params = [
    new NullableRangeParameter('Minimum number of words', false, 2, 1, MAX_NUM_WORDS),
    new NullableRangeParameter('Maximum number of words', false, 2, 1, MAX_NUM_WORDS),
    new NullableRangeParameter('Desired number of words', false, 2, 1, MAX_NUM_WORDS),
  ];

  static $_title = 'Number of Words';
  static $_description = 'Rank by total number of words';

  constructor() {
    super();
  }

  $_score(url: Url) {
    const minWords = this.$_params[0].$_value;
    const maxWords = this.$_params[1].$_value;
    const desiredNumWords = this.$_params[2].$_value;

    let totalScore = 0;
    let maxScore = 0;

    const urlLength = url.length(this);

    // Calculate score functions.
    if (minWords != null) {
      // Handle minimum words.
      if (urlLength < minWords) {
        // Negatively penalize when num words is under minimum.
        totalScore += clamp((urlLength - minWords) / MAX_NUM_WORDS, -1, 1);
      } else {
        totalScore++;
      }
      maxScore++;
    }

    if (maxWords != null) {
      // Handle maximum words.
      if (urlLength > maxWords) {
        // Negatively penalize when num words is above maximum.
        totalScore += clamp((maxWords - urlLength) / MAX_NUM_WORDS, -1, 1);
      } else {
        totalScore++;
      }
      maxScore++;
    }

    if (desiredNumWords != null) {
      // Handle desired number of words.
      if (urlLength != desiredNumWords) {
        // Negatively penalize when num words strays from desired length.
        totalScore += clamp(
          -Math.abs(urlLength - desiredNumWords) / MAX_NUM_WORDS,
          -1,
          1
        );
      } else {
        totalScore++;
      }
      maxScore++;
    }

    // Handle all scoring functions, with optional penalty.
    return totalScore / maxScore;
  }
}

export class WordLengthFilter extends Filter {
  $_params = [
    new NullableRangeParameter('Minimum word length', false, 3, 1, MAX_WORD_LENGTH),
    new NullableRangeParameter('Maximum word length', false, 8, 1, MAX_WORD_LENGTH),
    new NullableRangeParameter('Desired length', false, 4, 1, MAX_WORD_LENGTH),
  ];

  static $_title = 'Individual Word length';
  static $_description = 'Rank by individual word length';

  constructor() {
    super();
  }

  $_score(url: Url) {
    const minimumLength = this.$_params[0].$_value;
    const maximumLength = this.$_params[1].$_value;
    const desiredLength = this.$_params[2].$_value;

    let totalScore = 0;
    let maxScore = 0;
    url.iterate(this, urlWord => {
      const word = urlWord.label;
      if (minimumLength != null) {
        maxScore++;
        // Handle minimum length
        if (word.length < minimumLength) {
          // Negatively penalize words under minimum length.
          totalScore += clamp((word.length - minimumLength) / MAX_WORD_LENGTH, -1, 0);
        } else {
          totalScore++;
        }
      }

      if (maximumLength != null) {
        maxScore++;
        // Handle maximum length
        if (word.length > maximumLength) {
          // Negatively penalize words above maximum length.
          totalScore += clamp((maximumLength - word.length) / MAX_WORD_LENGTH, -1, 0);
        } else {
          totalScore++;
        }
      }

      if (desiredLength != null) {
        maxScore++;
        // Handle desired length
        if (word.length != desiredLength) {
          // Negatively penalize words that stray from desired length.
          totalScore += clamp(
            -Math.abs(desiredLength - word.length) / MAX_WORD_LENGTH / 2,
            -1,
            0
          );
        } else {
          totalScore++;
        }
      }
    });

    return totalScore / maxScore;
  }
}

const MAX_BALANCE = 10;

export class WordBalanceFilter extends Filter {
  $_params = [
    new NullableRangeParameter('Minimum balance', false, 3, 0, MAX_BALANCE),
    new NullableRangeParameter('Maximum balance', false, 0, 0, MAX_BALANCE),
    new NullableRangeParameter('Desired balance', false, 0, 0, MAX_BALANCE),
  ];

  static $_title = 'Word balance';
  static $_description = 'Balance of word lengths (0 = all words same length)';

  constructor() {
    super();
  }

  $_score(url: Url) {
    const minBalance = this.$_params[0].$_value;
    const maxBalance = this.$_params[1].$_value;
    const desiredBalance = this.$_params[2].$_value;

    let totalScore = 0;
    let maxScore = 0;

    let totalLength = 0;
    let totalWords = 0;
    url.iterate(this, word => {
      totalLength += word.label.length;
      totalWords++;
    });
    const avg = totalLength / totalWords;

    let balance = 0;
    url.iterate(this, word => {
      balance += Math.abs(word.label.length - avg);
    });
    balance /= totalWords;

    // Calculate score functions.
    if (minBalance != null) {
      // Handle minimum balance.
      if (balance < minBalance) {
        // Negatively penalize.
        totalScore += clamp((balance - minBalance) / MAX_BALANCE, -1, 1);
      } else {
        totalScore++;
      }
      maxScore++;
    }

    if (maxBalance != null) {
      // Handle maximum balance.
      if (balance > maxBalance) {
        // Negatively penalize.
        totalScore += clamp((maxBalance - balance) / MAX_BALANCE, -1, 1);
      } else {
        totalScore++;
      }
      maxScore++;
    }

    if (desiredBalance != null) {
      // Handle desired balance.
      if (balance != desiredBalance) {
        // Negatively penalize when total length strays from desired length.
        totalScore += clamp(-Math.abs(balance - desiredBalance) / MAX_BALANCE, -1, 1);
      } else {
        totalScore++;
      }
      maxScore++;
    }

    return totalScore / maxScore;
  }
}

export class AdjacentLetterFilter extends Filter {
  $_params = [];

  static $_title = 'No adjacent end/start letters';
  static $_description =
    'Discourage the same letter at end of one word and start of next';

  $_score(url: Url) {
    let score = 0;
    let max = 0;
    for (let i = 0; i < url.$_words.length; i++) {
      if (i != url.$_words.length - 1) {
        const word = url.$_words[i].label;
        const next = url.$_words[i + 1].label;

        if (word.charAt(word.length - 1) != next.charAt(0)) {
          score++;
        }
        max++;
      }
    }

    return clamp(score / max, 0, 1);
  }
}

export class AlliterationFilter extends Filter {
  $_params = [];

  static $_title = 'Alliteration';
  static $_description = 'Words that start with the same letter';

  $_score(url: Url) {
    const alliterations: {[key: string]: number} = {};

    let highest = 1;
    let max = 0;
    url.iterate(this, word => {
      const c = word.label.charAt(0);
      if (c in alliterations) {
        alliterations[c]++;
        if (alliterations[c] > highest) {
          highest = alliterations[c];
        }
      } else {
        alliterations[c] = 1;
      }

      max++;
    });

    return clamp(highest / max, 0, 1);
  }
}

export class SameEndLetterFilter extends Filter {
  $_params = [];

  static $_title = 'Same End Letter';
  static $_description = 'Words that end with the same letter';

  $_score(url: Url) {
    const endLetters: {[key: string]: number} = {};

    let highest = 1;
    let max = 0;
    url.iterate(this, word => {
      const c = word.label.charAt(word.label.length - 1);
      if (c in endLetters) {
        endLetters[c]++;
        if (endLetters[c] > highest) {
          highest = endLetters[c];
        }
      } else {
        endLetters[c] = 1;
      }

      max++;
    });

    return clamp(highest / max, 0, 1);
  }
}

export class FrequencyFilter extends Filter {
  $_params = [
    new NullableRangeParameter('Minimum frequency', false, 5, 0, 100),
    new NullableRangeParameter('Maximum frequency', false, 30, 0, 100),
    new NullableRangeParameter('Desired frequency', false, 30, 0, 100),
  ];

  static $_title = 'Word frequency';
  static $_description = 'Rank by frequency (0 most common, 100 least common)';

  constructor() {
    super();
  }

  $_score(url: Url, controller: Controller) {
    const minFrequency = this.$_params[0].$_value;
    const maxFrequency = this.$_params[1].$_value;
    const desiredFrequency = this.$_params[2].$_value;

    let totalScore = 0;
    let maxScore = 0;

    const numWords = controller.$_words.length;

    url.iterate(this, word => {
      const freq = (word.index / numWords) * 100;

      if (minFrequency != null) {
        // Handle minimum frequency.
        if (freq < minFrequency) {
          // Negatively penalize.
          totalScore += clamp((freq - minFrequency) / 100, -1, 1);
        } else {
          totalScore++;
        }
        maxScore++;
      }

      if (maxFrequency != null) {
        // Handle minimum frequency.
        if (freq > maxFrequency) {
          // Negatively penalize.
          totalScore += clamp((maxFrequency - freq) / 100, -1, 1);
        } else {
          totalScore++;
        }
        maxScore++;
      }

      if (desiredFrequency != null) {
        // Handle desired frequency.
        if (freq != desiredFrequency) {
          // Negatively penalize.
          totalScore += clamp(
            -Math.abs(freq - desiredFrequency) / MAX_NUM_CHARS,
            -1,
            1
          );
        } else {
          totalScore++;
        }
        maxScore++;
      }
    });

    return totalScore / maxScore;
  }
}
